<!-- 商品 Excel 导入：先下载模板 → 填表 → 上传；整表失败不落库 -->
<template>
  <Dialog v-model="dialogVisible" :title="title" width="640px">
    <el-alert
      :closable="false"
      class="mb-12px"
      show-icon
      type="info"
      :title="hint"
    />

    <div class="mb-12px flex items-center gap-12px">
      <el-button
        :loading="templateLoading"
        plain
        type="primary"
        @click="handleDownloadTemplate"
      >
        <Icon class="mr-5px" icon="ep:download" />
        下载模板
      </el-button>
      <el-text size="small" type="info">{{ templateTip }}</el-text>
    </div>

    <el-upload
      ref="uploadRef"
      v-model:file-list="fileList"
      :auto-upload="false"
      :disabled="formLoading"
      :limit="1"
      :on-exceed="handleExceed"
      accept=".xlsx, .xls"
      action="none"
      drag
    >
      <Icon icon="ep:upload" />
      <div class="el-upload__text">将填好的模板拖到此处，或<em>点击上传</em></div>
      <template #tip>
        <div class="el-upload__tip text-center">
          <span>仅允许 xls、xlsx。请使用本弹窗「下载模板」得到的文件回传；任一失败则整表不写入。</span>
        </div>
      </template>
    </el-upload>

    <div v-if="result" class="mt-16px">
      <el-alert
        :closable="false"
        :title="`成功 ${result.successCount} 条，失败 ${result.failureCount} 条`"
        :type="result.failureCount ? 'warning' : 'success'"
        show-icon
      />
      <el-table
        v-if="result.failures?.length"
        :data="result.failures"
        class="mt-12px"
        max-height="280"
        size="small"
        stripe
      >
        <el-table-column label="行号" prop="rowIndex" width="70" />
        <el-table-column label="业务键" min-width="120" prop="bizKey" show-overflow-tooltip />
        <el-table-column label="失败原因" min-width="200" prop="reason" show-overflow-tooltip />
      </el-table>
    </div>

    <template #footer>
      <el-button :loading="templateLoading" plain @click="handleDownloadTemplate">
        下载模板
      </el-button>
      <el-button @click="dialogVisible = false">关 闭</el-button>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">开始导入</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import type { UploadUserFile } from 'element-plus'
import download from '@/utils/download'

defineOptions({ name: 'ProductExcelImportForm' })

export interface ProductExcelImportFailure {
  rowIndex?: number
  bizKey?: string
  reason?: string
}

export interface ProductExcelImportResult {
  successCount: number
  failureCount: number
  failures?: ProductExcelImportFailure[]
}

const props = withDefaults(
  defineProps<{
    title: string
    /** 顶部说明 */
    hint: string
    /** 「下载模板」旁短提示 */
    templateTip?: string
    /** 下载模板文件名（含扩展名） */
    templateFileName: string
    /** 拉取模板 Blob */
    downloadTemplateApi: () => Promise<Blob>
    importApi: (formData: FormData) => Promise<ProductExcelImportResult>
  }>(),
  {
    templateTip: '请先下载模板，按说明填写后再上传'
  }
)

const message = useMessage()
const dialogVisible = ref(false)
const formLoading = ref(false)
const templateLoading = ref(false)
const uploadRef = ref()
const fileList = ref<UploadUserFile[]>([])
const result = ref<ProductExcelImportResult | null>(null)

const open = async () => {
  dialogVisible.value = true
  result.value = null
  fileList.value = []
  await nextTick()
  uploadRef.value?.clearFiles()
}
defineExpose({ open })

const emit = defineEmits<{ success: [] }>()

const handleDownloadTemplate = async () => {
  templateLoading.value = true
  try {
    const data = await props.downloadTemplateApi()
    download.excel(data, props.templateFileName)
    message.success('模板已下载')
  } catch {
    message.error('模板下载失败，请重试')
  } finally {
    templateLoading.value = false
  }
}

const submitForm = async () => {
  if (fileList.value.length === 0) {
    message.error('请先下载模板并填写后上传')
    return
  }
  const raw = fileList.value[0].raw
  if (!raw) {
    message.error('请重新选择文件')
    return
  }
  formLoading.value = true
  result.value = null
  try {
    const formData = new FormData()
    formData.append('file', raw)
    const data = await props.importApi(formData)
    result.value = data
    if (data.successCount > 0) {
      emit('success')
      message.success(`导入成功，共 ${data.successCount} 条`)
    } else if (data.failureCount) {
      message.warning(`整表未写入，失败 ${data.failureCount} 条，请查看明细`)
    } else {
      message.warning('文件无有效数据行')
    }
  } catch {
    message.error('导入失败，请检查文件后重试')
  } finally {
    formLoading.value = false
  }
}

const handleExceed = () => {
  message.error('最多只能上传一个文件')
}
</script>
